import moment from 'moment';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Payment } from '@baemingo/table-events/dist/types';
import Modal from '~Components/Modal';
import { useTableApi } from '~Components/TableUniverse';
import { useAdyen, useDispatch, useI18n, useStaffCan, useCurrency } from '~Hooks';
import Button from '~Scenes/Tables/Components/Button';
import { useStorage } from '~Components/Storage';
import { addToast, removeToast } from '~/actions';

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  margin: 8px 0;
  border-radius: 2px;
`;
const MetaDataWrapper = styled.div`
  flex: 2;
  margin: 10px 16px;
  /* padding-right: 80px; */
  display: flex;
`;
const MetaData = styled.span`
  display: inline-block;
  margin-right: 16px;
  flex: 1 1 0px;
`;

const MetaDataTitle = styled.span`
  font-size: 14px;
  opacity: 0.7;
  margin-right: 6px;
  display: inline-block;
`;

const Actions = styled.div`
  display: flex;
  flex: 1;
`;
const RefundButton = styled(Button)`
  background-color: #209946;
`;
const VoidButton = styled(Button)`
  background-color: #951e1f;
`;

interface Props {
  payment: Payment;
  chequeId: string;
}

const ChequePayment = ({ payment, chequeId }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const { refundPayment: adyenRefund } = useAdyen();
  const { refundPayment: tableRefund, removePayment } = useTableApi();
  const [storage] = useStorage();
  const { adyenTerminals } = storage;
  const currency = useCurrency();

  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const staffCan = useStaffCan();
  const [showTerminals, setShowTerminals] = useState(false);

  const refund = (terminalId: string) => {
    setLoading(true);

    dispatch(addToast(i18n('TOAST.Refund.Progress'), false, payment.id));

    adyenRefund({
      transactionId: payment.transactionId!,
      createdAt: payment.createdAt,
      terminalId,
    }).then(({ success, message }) => {
      if (success) {
        dispatch(removeToast(payment.id));
        dispatch(addToast(i18n('TOAST.Refund.Successful'), true));
        tableRefund(payment.id, chequeId);
      } else {
        dispatch(removeToast(payment.id));
        dispatch(addToast(i18n('TOAST.Refund.Failed'), true));
        alert('Refund failed ' + message);
      }
      setLoading(false);
    });
  };

  const remove = () => {
    removePayment(payment.id);
    setLoading(false);
  };

  return (
    <Wrapper>
      <MetaDataWrapper>
        <MetaData>
          <MetaDataTitle>{i18n('History.Amount')}</MetaDataTitle>
          {currency.format(payment.amount)}
        </MetaData>
        <MetaData>
          <MetaDataTitle>{i18n('History.Type')}</MetaDataTitle>
          {payment.transactionId}
        </MetaData>
        <MetaData>
          <MetaDataTitle>{i18n('History.Paid')}</MetaDataTitle>
          {moment(payment.createdAt).format('HH:mm')}
        </MetaData>
        <MetaData>
          {payment.refunded && (
            <>
              <MetaDataTitle>{i18n('History.Status')}</MetaDataTitle> {i18n('History.Refunded')}
            </>
          )}
        </MetaData>
      </MetaDataWrapper>
      <Actions>
        <RefundButton
          onClick={() => {
            setShowTerminals(true);
          }}
          disabled={loading || payment.type !== 'ADYEN' || payment.refunded || !staffCan.REFUND}>
          {loading ? i18n('History.Refunding') : i18n('History.Refund')}
        </RefundButton>
        <VoidButton
          onClick={() => {
            setShowConfirm(true);
          }}
          disabled={loading || !staffCan.VOID_PAYMENT}>
          {i18n('History.Void')}
        </VoidButton>
      </Actions>

      <Modal
        show={showConfirm}
        onConfirm={() => {
          remove();
          setShowConfirm(false);
        }}
        onClose={() => {
          setShowConfirm(false);
        }}
        agreeButton={i18n('History.Confirm')}
        disagreeButton={i18n('History.Cancel')}
        title={i18n('History.VoidPayment')}
        content={i18n('History.ActionIrreversible')}></Modal>

      <Modal
        title={i18n('History.ChooseRefundTerminal')}
        show={showTerminals}
        onClose={() => setShowTerminals(false)}>
        {adyenTerminals.map((terminal) => (
          <RefundButton
            key={terminal.POIID}
            onClick={() => {
              refund(terminal.POIID);
              setShowTerminals(false);
            }}>
            {terminal.POIID}
          </RefundButton>
        ))}
      </Modal>
    </Wrapper>
  );
};

export default ChequePayment;
